import { useImperativeHandle, useRef } from "react";

import {
  FieldDefinitionFieldType,
  FieldDefinitionPathType,
  FieldImperativeHandle,
} from "@/components/@types";
import { Values } from "@/constants/values";
import { Skeleton } from "@/components/ui/skeleton";
import { typographyStyles } from "@/components/ui/typography";

function FieldPathValue<T extends FieldDefinitionPathType<T>>({
  value,
  style,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionPathType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const pathRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        pathRef.current?.focus();
      },
      unsetFocus: () => {
        pathRef.current?.blur();
      },
    }),
    [],
  );

  return (
    <div
      ref={pathRef}
      tabIndex={0}
      style={
        {
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: `${"0px"} ${"8px"}`,
          ...(typographyStyles["mono-body-sm"] as React.CSSProperties),
          color: "var(--foreground)",
          outline: "none",
          ...style,
        } as React.CSSProperties
      }
    >
      {value?.split("/").map((segment, index) => (
        <span key={index}>
          {index > 0 && (
            <span
              style={{
                color:
                  "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
              }}
            >
              /
            </span>
          )}
          <span>{segment}</span>
        </span>
      ))}
    </div>
  );
}

function FieldPathSkeleton() {
  return (
    <Skeleton
      variant="text"
      level="body-md"
      style={{
        display: undefined,
        width: "75%",
        maxWidth: Values.DEFAULT_COLUMN_WIDTH,
      }}
    />
  );
}

export function FieldPath<T extends FieldDefinitionPathType<T>>({
  isLoading,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionPathType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldPathSkeleton />;
  }

  return <FieldPathValue {...props} ref={ref} />;
}
